import BaseComponent from '@js/BaseComponent'
import {
    hasMotion,
    onHasMotion,
    hasMouse
} from '@js/mediaQueries'

/**
 * Controller for the spotlight effect
 *
 * @extends BaseComponent
 */
class Spotlight extends BaseComponent {
    inInView = false

    constructor(element) {
        super(element)

        if (!hasMouse()) return

        if (this.effectActive) {
            this.addEvents()
        }

        onHasMotion(this.updateEvents)
    }

    /**
     * Tracks whether the effect should be active or not, based on motion and
     * whether the element is in view
     * @type {bool}
     */
    get effectActive() {
        return hasMotion() && this.isInView
    }

    /**
     * Calls addEvents() or removeEvents() based on effectActive
     */
    updateEvents = () => {
        if (this.effectActive) {
            this.addEvents()
        } else {
            this.removeEvents()
        }
    }

    /**
     * Adds mousemove when motion is enabled and element is in view
     */
    addEvents() {
        this.element.classList.add('active')
        document.addEventListener('mousemove', this.trackMouseMove)
    }

    /**
     * Removes mousemove when motion is disabled or element is out of view
     */
    removeEvents() {
        this.element.classList.remove('active')
        document.removeEventListener('mousemove', this.trackMouseMove)
    }

    /**
     * Callback for the mousemove event.
     *
     * Sets --spotlight-x and --spotlight-y CSS variables.
     *
     * @param {MouseEvent} event
     */
    trackMouseMove = (event) => {
        const {
            left: rectLeft,
            top: rectTop
        } = this.element.getBoundingClientRect()

        const left = event.clientX - rectLeft
        const top = event.clientY - rectTop

        this.element.style.setProperty('--spotlight-x', `${left}px`)
        this.element.style.setProperty('--spotlight-y', `${top}px`)
    }
}

/**
 * Adds IntersectionObserver to track all Spotlight objects
 * @param {Spotlight[]} objects
 */
Spotlight.onAfterCreate = (objects) => {
    const options = {
        rootMargin: '0px',
        threshold: [0],
    }

    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (!hasMouse()) return

            const {
                isIntersecting,
                target
            } = entry

            const spotlight = sir.components[target.dataset.componentid]

            spotlight.isInView = isIntersecting
            spotlight.updateEvents()
        })
    }, options)

    objects.forEach(({ element }) => {
        observer.observe(element)
    })
}

Spotlight.create('.spotlight, .spotlight--shadow')
